import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dismiss16Regular, ArrowMaximize16Regular, ArrowMinimize16Regular } from '@fluentui/react-icons';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { clearFinishedUploadTrayItems, dismissUploadTray, setUploadTrayCollapsed, removeUploadTrayItem } from '../../redux/documentsSlice';
import { getExtensionIcon } from '../../utils/commonUtils';
import { getFileExtension } from '../../utils/vdrUploadUtils';
import styles from './css/index.module.css';

const STATUS_LABEL = {
    queued: 'Queued',
    uploading: 'Uploading',
    indexing: 'Indexing for AI',
    ready: 'Successful',
    failed: 'Failed',
    skipped: 'Skipped',
};

const GENERIC_FAILED_MESSAGE = 'Upload failed. Please try again.';

const displayError = error => {
    if (!error || typeof error !== 'string') return GENERIC_FAILED_MESSAGE;
    const text = error.trim();
    if (!text || text.length > 180 || /<!DOCTYPE\s+html/i.test(text) || /<html[\s>]/i.test(text)) {
        return GENERIC_FAILED_MESSAGE;
    }
    return text;
};

const UploadProgressTray = () => {
    const dispatch = useDispatch();
    const { items, collapsed, destinationLabel } = useSelector(state => state?.documents?.uploadTray || {});
    const list = items || [];

    React.useEffect(() => {
        if (!list.some(item => item.status === 'ready' || item.status === 'skipped')) return undefined;
        const timer = setTimeout(() => {
            dispatch(clearFinishedUploadTrayItems());
        }, 4500);
        return () => clearTimeout(timer);
    }, [dispatch, list]);

    if (!list.length) return null;

    const doneCount = list.filter(item => ['ready', 'skipped', 'failed'].includes(item.status)).length;
    const activeCount = list.filter(item => ['queued', 'uploading', 'indexing'].includes(item.status)).length;
    const failedCount = list.filter(item => item.status === 'failed').length;
    const fileCountLabel = `${list.length} file${list.length === 1 ? '' : 's'}`;
    const stillStarting = doneCount === 0 && activeCount > 0;
    const progressText = stillStarting
        ? `${fileCountLabel} uploading`
        : `${doneCount} / ${list.length}${failedCount ? ` · ${failedCount} failed` : ''}${activeCount ? ` · ${activeCount} in progress` : ''}`;
    const collapsedMeta = stillStarting
        ? `${fileCountLabel} uploading`
        : `${doneCount}/${list.length}${failedCount ? ` · ${failedCount} failed` : activeCount ? ' · working' : ''}`;

    if (collapsed) {
        return (
            <button
                type="button"
                className={styles.uploadTrayCollapsed}
                onClick={() => dispatch(setUploadTrayCollapsed(false))}
                aria-label={stillStarting ? `Expand upload progress, ${fileCountLabel} uploading` : `Expand upload progress, ${doneCount} of ${list.length} complete`}
                title="Expand upload progress">
                {activeCount > 0 ? (
                    <AiOutlineLoading3Quarters className={`rotateLoader ${styles.uploadTrayCollapsedSpinner}`} />
                ) : (
                    <span className={styles.uploadTrayCollapsedDot} />
                )}
                <span className={styles.uploadTrayCollapsedText}>
                    <span className={styles.uploadTrayCollapsedTitle}>Uploads</span>
                    <span className={styles.uploadTrayCollapsedMeta}>{collapsedMeta}</span>
                </span>
                <ArrowMaximize16Regular className={styles.uploadTrayCollapsedChevron} />
            </button>
        );
    }

    return (
        <div className={styles.uploadTray} role="status" aria-live="polite">
            <div className={styles.uploadTrayAccent} />
            <div className={styles.uploadTrayHeader}>
                <div className={styles.uploadTrayHeaderText}>
                    <span className={styles.uploadTrayTitle}>Upload progress</span>
                    <span className={styles.uploadTrayCount}>{progressText}</span>
                    {destinationLabel ? <span className={styles.uploadTrayDest}>{destinationLabel}</span> : null}
                </div>
                <div className={styles.uploadTrayHeaderActions}>
                    <button type="button" className={styles.uploadTrayIconBtn} onClick={() => dispatch(setUploadTrayCollapsed(true))} aria-label="Collapse upload progress">
                        <ArrowMinimize16Regular />
                    </button>
                    {activeCount === 0 ? (
                        <button type="button" className={styles.uploadTrayIconBtn} onClick={() => dispatch(dismissUploadTray())} aria-label="Dismiss upload progress">
                            <Dismiss16Regular />
                        </button>
                    ) : null}
                </div>
            </div>
            <ul className={styles.uploadTrayList}>
                {list.map(item => (
                    <li key={item.clientId} className={styles.uploadTrayRow}>
                        <span className={styles.uploadTrayFileIcon} aria-hidden>
                            {getExtensionIcon(getFileExtension(item.fileName), 18, 18)}
                        </span>
                        <div className={styles.uploadTrayRowMain}>
                            <span className={styles.uploadTrayFileName} title={item.fileName}>
                                {item.fileName}
                            </span>
                            <span
                                className={`${styles.uploadTrayStatus} ${
                                    item.status === 'failed'
                                        ? styles.uploadTrayStatusFailed
                                        : item.status === 'ready' || item.status === 'skipped'
                                          ? styles.uploadTrayStatusReady
                                          : styles.uploadTrayStatusActive
                                }`}>
                                {['queued', 'uploading', 'indexing'].includes(item.status) ? (
                                    <AiOutlineLoading3Quarters className={`rotateLoader ${styles.uploadTraySpinner}`} />
                                ) : null}
                                <span
                                    className={styles.uploadTrayStatusText}
                                    title={item.status === 'failed' ? displayError(item.error) : STATUS_LABEL[item.status] || item.status}>
                                    {item.status === 'failed' ? displayError(item.error) : STATUS_LABEL[item.status] || item.status}
                                </span>
                            </span>
                        </div>
                        {item.status === 'failed' ? (
                            <button
                                type="button"
                                className={styles.uploadTrayIconBtn}
                                onClick={() => dispatch(removeUploadTrayItem(item.clientId))}
                                aria-label={`Dismiss ${item.fileName}`}>
                                <Dismiss16Regular />
                            </button>
                        ) : null}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default UploadProgressTray;
